import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { TableColumns } from '@aid/shared/types/interfaces/table-columns.interface';

@Component({
  selector: 'aid-data-table',
  template: `
    <div style="max-height: 100%" class="aid-layout-column">
      <div class="container full-height aid-layout-column aid-flex-fill">
        @if (enableFiltering && dataSource && dataSource.data.length !== 0) {
          <mat-form-field class="full-width">
            <mat-label>{{ filterLabel | translate }}</mat-label>
            <input
              matInput
              #filterInput
              (keyup)="applyFilter(filterInput.value)"
            />
          </mat-form-field>
        }

        <div
          [ngStyle]="{
            'max-height': tableHeight ? tableHeight + 'px' : '100%'
          }"
          style="overflow: auto; position: relative"
          class="aid-layout-row"
        >
          <table mat-table [dataSource]="dataSource" matSort class="full-width">
            @if (showIndex) {
              <ng-container matColumnDef="index">
                <th mat-header-cell *matHeaderCellDef mat-sort-header>#</th>
                <td mat-cell *matCellDef="let element; let i = index">
                  {{ i + 1 }}
                </td>
              </ng-container>
            }

            @for (column of tableColumns | keyvalue; track column) {
              <ng-container matColumnDef="{{ column.key }}">
                @if (column.key === 'actions') {
                  <th mat-header-cell *matHeaderCellDef></th>
                  <td mat-cell *matCellDef="let element" class="action-column">
                    @if (canEdit || canRemove) {
                      <button
                        mat-icon-button
                        [matMenuTriggerFor]="menu"
                        (click)="
                          $event.stopPropagation(); activeElement.emit(element)
                        "
                      >
                        <mat-icon svgIcon="more_vert"></mat-icon>
                      </button>
                    }
                    <mat-menu #menu="matMenu">
                      <ng-content select="[mlTableOptions]"></ng-content>
                      @if (canEdit) {
                        <button mat-menu-item (click)="edit.emit(element)">
                          <mat-icon matPrefix svgIcon="edit"></mat-icon>
                          {{ 'edit' | translate | titlecase }}
                        </button>
                      }
                      @if (canRemove) {
                        <button mat-menu-item (click)="remove.emit(element)">
                          <mat-icon
                            matPrefix
                            svgIcon="delete"
                            color="warn"
                          ></mat-icon>
                          {{ 'remove' | translate | titlecase }}
                        </button>
                      }
                    </mat-menu>
                  </td>
                }
                @if (column.key !== 'actions') {
                  <th mat-header-cell *matHeaderCellDef>
                    {{ column.value.title | translate | uppercase }}
                  </th>
                  <td mat-cell *matCellDef="let element">
                    @if (!column.value.isArray) {
                      <div>
                        @if (column.value.displayFn) {
                          <div>
                            {{
                              column.value.translate
                                ? (column.value.displayFn(element) | translate)
                                : column.value.displayFn(element)
                            }}
                          </div>
                        } @else {
                          {{
                            element[column.key]
                              ? column.value.translate
                                ? (element[column.key] | translate)
                                : element[column.key]
                              : '-'
                          }}
                        }
                      </div>
                    } @else {
                      @for (item of element[column.key]; track item) {
                        <div>
                          @if (column.value.displayFn) {
                            <div>
                              {{ item ? column.value.displayFn(item) : '-' }}
                            </div>
                          } @else {
                            {{ item ? item : '-' }}
                          }
                        </div>
                      }
                      @if (element[column.key].length === 0) {
                        <div>-</div>
                      }
                    }
                  </td>
                }
              </ng-container>
            }

            <tr
              mat-header-row
              *matHeaderRowDef="displayedColumns; sticky: true"
            ></tr>
            <tr
              [ngClass]="{ selectable: selectable }"
              (click)="onSelect(row)"
              mat-row
              *matRowDef="let row; columns: displayedColumns"
            ></tr>
          </table>
        </div>
        <div
          [hidden]="
            !(
              dataSource &&
              enablePagination &&
              !loading &&
              dataSource.filteredData.length !== 0
            )
          "
        >
          <mat-paginator
            [pageSizeOptions]="[10, 20, 30, 50]"
            [pageSize]="10"
          ></mat-paginator>
        </div>
        @if (
          (!dataSource ||
            dataSource.filteredData.length === 0 ||
            dataSource.data.length === 0) &&
          !loading
        ) {
          <div>
            <h4 class="text-center">
              {{ emptyText | translate | sentancecase }}
            </h4>
          </div>
        }
        @if (loading) {
          <div>
            <mat-spinner color="primary" style="margin: auto"></mat-spinner>
          </div>
        }
      </div>
    </div>
  `,
  styleUrls: ['./data-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class DataTableComponent implements OnInit {
  @Input() loading?: boolean;
  @Input() enablePagination = true;
  @Input() enableFiltering = false;
  @Input({ required: true }) tableColumns: TableColumns = {};
  @Input() canAdd = false;
  @Input() tableHeight?: number;
  @Input() canEdit = false;
  @Input() canRemove = false;
  @Input() selectable = false;
  @Input() filterLabel = 'Filter';
  @Input() showIndex = false;
  @Input() emptyText?: string;

  @Input() set data(data: any[] | null | undefined) {
    if (data) {
      this.dataSource = new MatTableDataSource(data);
      this.dataSource.sort = this.sort ?? null;
      if (this.enablePagination) {
        this.dataSource.paginator = this.paginator ?? null;
      }
    }
  }

  displayedColumns: string[] = [];
  dataSource = new MatTableDataSource<any>();

  /**
   * Used to emit the row in which the user clicks the 3 dots
   */
  @Output() activeElement = new EventEmitter();
  @Output() edit: EventEmitter<any> = new EventEmitter();
  @Output() remove: EventEmitter<any> = new EventEmitter();
  @Output() add: EventEmitter<void> = new EventEmitter();
  @Output() select: EventEmitter<any> = new EventEmitter();

  @ViewChild(MatSort, { static: true }) sort?: MatSort;
  @ViewChild(MatPaginator, { static: true }) paginator?: MatPaginator;

  constructor() {}

  ngOnInit() {
    this.displayedColumns = Object.keys(this.tableColumns);
    if (this.showIndex) {
      this.displayedColumns = ['index', ...this.displayedColumns];
    }
  }

  applyFilter(filterValue: string) {
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  onSelect(row: unknown) {
    if (this.selectable) {
      this.select.emit(row);
    }
  }
}
